/**
 * useUndoableMutation - React Query mutation wrapper with undo support
 *
 * Wraps mutations to capture inverse data and push operations to undo stack.
 */

import { useCallback, useRef, useState } from 'react'
import { useUndo } from './UndoContext'
import {
	generateOperationId,
	type BlockMutationType,
	type InverseOperationData,
	type RedoOperationData,
	type UndoableOperation,
} from './Types'

// ========== Types ==========

export interface UseUndoableMutationOptions<TVariables, TResult> {
	/** Type of mutation for the undo system */
	mutationType: BlockMutationType
	/** Human-readable label for the undo UI */
	label: string
	/**
	 * Get inverse data before mutation executes.
	 * May be async if you need to fetch current values first.
	 */
	getInverseData: (variables: TVariables) => InverseOperationData | Promise<InverseOperationData>
	/**
	 * Get redo data from variables.
	 * Called after mutation succeeds.
	 */
	getRedoData: (variables: TVariables, result: TResult) => RedoOperationData
	/** The actual mutation function */
	mutationFn: (variables: TVariables) => Promise<TResult>
}

export interface UndoableMutationResult<TVariables, TResult> {
	/** Execute the mutation */
	mutate: (variables: TVariables, options?: { transactionId?: string }) => Promise<TResult>
	/** Whether mutation is in progress */
	isPending: boolean
	/** Error from last mutation */
	error: Error | null
}

// ========== Hook ==========

export function useUndoableMutation<TVariables, TResult>(
	options: UseUndoableMutationOptions<TVariables, TResult>
): UndoableMutationResult<TVariables, TResult> {
	const { mutationType, label, getInverseData, getRedoData, mutationFn } = options
	const { pushOperation, currentTransactionId } = useUndo()

	// Use state for values that should trigger re-renders
	const [isPending, setIsPending] = useState(false)
	const [error, setError] = useState<Error | null>(null)

	// Use ref to track options that shouldn't trigger re-renders
	const optionsRef = useRef({ mutationType, label, getInverseData, getRedoData, mutationFn })
	optionsRef.current = { mutationType, label, getInverseData, getRedoData, mutationFn }

	const mutate = useCallback(
		async (
			variables: TVariables,
			mutationOptions?: { transactionId?: string }
		): Promise<TResult> => {
			setIsPending(true)
			setError(null)

			try {
				const opts = optionsRef.current
				// Capture inverse data BEFORE mutation
				const inverseData = await opts.getInverseData(variables)

				// Execute the mutation
				const result = await opts.mutationFn(variables)

				// Get redo data AFTER mutation succeeds
				const redoData = opts.getRedoData(variables, result)

				// Create the undoable operation
				const operation: UndoableOperation = {
					id: generateOperationId(),
					timestamp: Date.now(),
					type: opts.mutationType,
					label: opts.label,
					inverseData,
					redoData,
					transactionId: mutationOptions?.transactionId ?? currentTransactionId ?? undefined,
				}

				// Push to undo stack
				pushOperation(operation)

				setIsPending(false)
				return result
			} catch (e) {
				setIsPending(false)
				const err = e instanceof Error ? e : new Error(String(e))
				setError(err)
				throw err
			}
		},
		[pushOperation, currentTransactionId]
	)

	return {
		mutate,
		isPending,
		error,
	}
}
